import { useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { mdiGiftOutline } from "@mdi/js";
import Icon from "@mdi/react";

import SecretSantaApi, { SECRET_SANTA_ACTIONS } from "api/secret-santa";
import CircularLoader from "components/CircularLoader";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import { useShallow } from "zustand/shallow";

export interface GeneratePairsProps {
  eventId?: number;
  groupId?: number;
}

export default function GeneratePairs(props: GeneratePairsProps) {
  const { eventId, groupId } = props;
  const [isGenerating, setIsGenerating] = useState(false);
  const votes = useBoundStore((state) => state.appData.votes);
  const selected = useBoundStore(
    useShallow((state) => ({
      set: state.selected.set,
      activeApps: state.selected.eventApps,
    }))
  );
  const { showNotification } = useNotifier();
  const secretSantaApi = useMemo(() => new SecretSantaApi(showNotification), [showNotification]);
  const participantsCount = votes.filter((vote) => vote.event_id === eventId && vote.type === "participating").length;

  async function handleGeneratePairs() {
    if (participantsCount < 3) {
      return showNotification({
        message: "To start a Secret Santa, an event must have at least 3 participants.",
        severity: "warning",
      });
    }

    if (eventId && groupId) {
      setIsGenerating(true);
      const { error } = await secretSantaApi.manageSecretSanta(eventId, groupId, SECRET_SANTA_ACTIONS.START);
      setIsGenerating(false);

      if (!error) {
        selected.set({
          eventApps: [...(selected.activeApps ?? []), "secret-santa"],
        });
        showNotification({
          message: "Secret Santa pairs successfully generated.",
          severity: "success",
        });
      }
    }

    return undefined;
  }

  return (
    <Box
      alignItems='center'
      display='flex'
      flexDirection='column'
      p={2}
      position='absolute'
      pt={4}
      sx={{ transform: "translateY(-50%)" }}
      top='50%'
      width='100%'
    >
      <Icon path={mdiGiftOutline} size={2.5} />
      <Typography align='center' gutterBottom sx={{ mt: 2 }}>
        Once the pairs are generated, participants are
        <Typography component='span' sx={{ color: "primary.main", fontWeight: "bold" }}>
          {" "}
          unable to leave the{" "}
        </Typography>
        event. If new users join after the pairings are generated,
        <Typography component='span' sx={{ color: "primary.main", fontWeight: "bold" }}>
          {" "}
          they won&#39;t be included{" "}
        </Typography>
        in Secret Santa.
      </Typography>

      <Box mt={5}>
        <CircularLoader loading={isGenerating}>
          <Button
            disabled={isGenerating}
            size='large'
            startIcon={<Icon path={mdiGiftOutline} size={1} />}
            variant='contained'
            onClick={handleGeneratePairs}
          >
            Generate pairs
          </Button>
        </CircularLoader>
      </Box>
    </Box>
  );
}
